import { Link, Outlet } from 'react-router-dom';

import { useActiveOrg } from '../active-org.ts';
import { useAuth } from '../api.ts';

/**
 * Context wrapper shared by every settings area:
 *   /settings/profile, /settings/team   — personal account pages
 *   /setup/*                            — org configuration (integrations,
 *                                         manage orgs, platform map, …)
 *
 * The chrome — nav rail, logo, org switcher — lives in {@link Layout},
 * which detects settings routes and swaps its middle nav for the settings
 * list (so the user stays inside the same app shell). This wrapper only
 * resolves the active org and hands `{ orgSlug, role }` to each page via
 * the router Outlet context. With no active org we show a hint to pick or
 * create one. Manage Orgs spans all orgs and reads them from auth instead.
 */
export function SettingsContent() {
  const auth = useAuth();
  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];
  const { active } = useActiveOrg(orgs);

  if (auth.status !== 'signed-in') {
    return <p className="muted">Sign in to manage settings.</p>;
  }
  if (!active) {
    return (
      <p className="muted">
        Pick or create an org to manage settings. <Link to="/">Go home</Link>
      </p>
    );
  }

  return (
    <Outlet
      context={{ orgSlug: active.slug, role: active.role } satisfies SettingsOutletContext}
    />
  );
}

export interface SettingsOutletContext {
  orgSlug: string;
  role: 'admin' | 'member';
}
